import React, { useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useSearchParams } from "react-router-dom";
import { Eye, Check, Send, Pencil, RefreshCw, CheckCircle2, XCircle, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { fetchAllPages } from "@/lib/serverExport";
import { http } from "@/lib/api";
import { useAuth, hasPerm } from "@/context/AuthContext";
import { useResourceList } from "@/hooks/useResource";
import { useOptions } from "@/hooks/useOptions";
import { userOptions } from "@/lib/users";
import { PageHeader } from "@/components/kit/PageHeader";
import { DataTablePro } from "@/components/kit/DataTablePro";
import { StatusBadge } from "@/components/kit/StatusBadge";
import { IdCell } from "@/components/kit/IdCell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { BulkPostbackDialog } from "@/drawers/BulkPostbackDialog";
import { ConversionBulkEditDialog } from "@/drawers/ConversionBulkEditDialog";
import { ConversionOwnerDialog } from "@/drawers/ConversionOwnerDialog";
import { DateRangePicker } from "@/components/kit/DateRangePicker";
import { ClickIdListFilter, parseClickIds, parseOrderIds } from "@/components/kit/ClickIdListFilter";
import { fmtMoney, fmtDateTime, shortId, getDisplayTimezone } from "@/lib/format";
import { useTenantColumn } from "@/hooks/useTenantColumn";
import { useTenants } from "@/hooks/useTenants";

const asOpts = (arr) => (arr || []).map((o) => (typeof o === "string" ? { value: o, label: o } : o));
const SUB_KEYS = Array.from({ length: 9 }, (_, i) => `sub_id_${i + 1}`);

function DetailRow({ label, value, mono }) {
  return (
    <div className="flex items-start justify-between gap-4 border-b border-white/[0.05] py-1.5 last:border-0">
      <span className="shrink-0 text-xs text-muted-foreground">{label}</span>
      <span className={`max-w-[68%] break-all text-right text-xs ${mono ? "font-mono" : ""} ${value ? "text-foreground" : "text-muted-foreground/50"}`}>{value || "—"}</span>
    </div>
  );
}
function Section({ title, children }) {
  return <div className="rounded-lg border border-white/[0.08] bg-[#0A0A0A] p-3"><p className="mb-1.5 text-[11px] font-semibold uppercase tracking-widest text-muted-foreground/70">{title}</p>{children}</div>;
}

const toLocalInput = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "";
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};
const fromLocalInput = (v) => (v ? new Date(v).toISOString() : "");
const inp = "h-8 w-full rounded-md border border-white/[0.12] bg-[#0A0A0A] px-2 text-xs text-foreground outline-none focus:border-white/25";

function EditField({ label, children }) {
  return (
    <div className="flex items-center justify-between gap-3 border-b border-white/[0.05] py-1.5 last:border-0">
      <span className="shrink-0 text-xs text-muted-foreground">{label}</span>
      <div className="w-[58%]">{children}</div>
    </div>
  );
}

function ConversionDetail({ id, staff, perms = {}, onClose }) {
  const canEditAny = staff && (perms.money || perms.date || perms.subs || perms.type || perms.payment);
  const qc = useQueryClient();
  const { data } = useQuery({ queryKey: ["conversion-detail", id], queryFn: async () => (await http.get(`/conversions/${id}/detail`)).data, enabled: !!id });
  const conv = data?.conversion || {};
  const click = data?.click || {};
  const af = click.antifraud || {};
  const afVal = (v) => (af.checked ? (v ? "Yes" : "No") : "Not checked");

  const [form, setForm] = useState(null);
  const [busy, setBusy] = useState(false);
  React.useEffect(() => {
    if (data?.conversion) {
      const c = data.conversion;
      setForm({
        created_at: toLocalInput(c.created_at), revenue: c.revenue ?? "", payout: c.payout ?? "",
        status: c.status || "lead", payment_status: c.payment_status || "hold",
        ...Object.fromEntries(SUB_KEYS.map((k) => [k, c[k] || ""])),
      });
    }
  }, [data]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const isLead = form && form.status === "lead";
  const profitPreview = !form || isLead ? "0.00" : (Number(form.revenue || 0) - Number(form.payout || 0)).toFixed(2);

  const save = async () => {
    const item = { id };
    if (form.created_at && form.created_at !== toLocalInput(conv.created_at)) item.created_at = fromLocalInput(form.created_at);
    if (String(form.revenue) !== String(conv.revenue ?? "")) item.revenue = form.revenue;
    if (String(form.payout) !== String(conv.payout ?? "")) item.payout = form.payout;
    if (form.status !== conv.status) item.status = form.status;
    if (form.payment_status !== conv.payment_status) item.payment_status = form.payment_status;
    SUB_KEYS.forEach((k) => { if ((form[k] || "") !== (conv[k] || "")) item[k] = form[k] || ""; });
    if (Object.keys(item).length === 1) return toast.info("No changes to save");
    setBusy(true);
    try {
      const { data: res } = await http.post("/conversions/bulk-edit", { items: [item] });
      qc.invalidateQueries({ queryKey: ["conversions"] });
      qc.invalidateQueries({ queryKey: ["conversion-detail", id] });
      qc.invalidateQueries({ queryKey: ["balances"] });
      qc.invalidateQueries({ queryKey: ["dashboard"] });
      qc.invalidateQueries({ queryKey: ["statistics"] });
      qc.invalidateQueries({ queryKey: ["stats-report"] });
      toast.success("Conversion updated", { description: `${res.changed} change(s) saved` });
    } catch (e) {
      toast.error("Update failed", { description: e?.response?.data?.detail || "Try again." });
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={!!id} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl border-white/[0.1] bg-[#141417]" data-testid="conversion-detail-dialog">
        <DialogHeader><DialogTitle className="flex items-center gap-2 font-mono">Conversion {conv.order_id} <StatusBadge status={conv.status} /> <StatusBadge status={conv.payment_status} /></DialogTitle></DialogHeader>
        <div className="grid max-h-[72vh] gap-3 overflow-y-auto p-0.5 sm:grid-cols-2">
          <Section title={canEditAny ? "Money & Status (editable)" : "Money"}>
            {canEditAny && form ? (
              <>
                {perms.money ? (
                  <>
                    <EditField label="Revenue"><input type="number" disabled={isLead} value={isLead ? 0 : form.revenue} onChange={(e) => set("revenue", e.target.value)} className={`${inp} text-right disabled:opacity-40`} data-testid="edit-revenue" /></EditField>
                    <EditField label="Payout"><input type="number" disabled={isLead} value={isLead ? 0 : form.payout} onChange={(e) => set("payout", e.target.value)} className={`${inp} text-right disabled:opacity-40`} data-testid="edit-payout" /></EditField>
                    <DetailRow label="Profit (preview)" value={fmtMoney(profitPreview)} mono />
                    {isLead && <p className="py-1 text-[11px] text-muted-foreground/70">Lead = registration only — no payout/revenue. Switch to Sale to auto-apply offer/override pricing.</p>}
                  </>
                ) : (
                  <>
                    <DetailRow label="Revenue" value={fmtMoney(conv.revenue)} mono />
                    <DetailRow label="Payout" value={fmtMoney(conv.payout)} mono />
                    <DetailRow label="Profit" value={fmtMoney(conv.profit)} mono />
                  </>
                )}
                {perms.type ? (
                  <EditField label="Conversion type">
                    <select value={form.status} onChange={(e) => set("status", e.target.value)} className={inp} data-testid="edit-status">
                      <option value="lead">Lead</option><option value="sale">Sale</option><option value="redep">Redep</option><option value="revshare">RevShare</option>
                    </select>
                  </EditField>
                ) : <DetailRow label="Conversion type" value={conv.status} />}
                {perms.payment ? (
                  <EditField label="Payment status">
                    <select value={form.payment_status} onChange={(e) => set("payment_status", e.target.value)} className={inp} data-testid="edit-payment">
                      <option value="hold">Hold</option><option value="approved">Approved</option><option value="trash">Trash</option>
                    </select>
                  </EditField>
                ) : <DetailRow label="Payment status" value={conv.payment_status} />}
                {perms.date ? (
                  <EditField label="Conversion date/time"><input type="datetime-local" value={form.created_at} onChange={(e) => set("created_at", e.target.value)} className={inp} data-testid="edit-date" /></EditField>
                ) : <DetailRow label="Conversion date/time" value={fmtDateTime(conv.created_at)} />}
              </>
            ) : (
              <>
                <DetailRow label="Payout" value={fmtMoney(conv.payout)} mono />
                {staff && <DetailRow label="Revenue" value={fmtMoney(conv.revenue)} mono />}
                {staff && <DetailRow label="Profit" value={fmtMoney(conv.profit)} mono />}
                <DetailRow label="Conversion type" value={conv.status} />
                <DetailRow label="Payment status" value={conv.payment_status} />
              </>
            )}
          </Section>
          <Section title="Tracking">
            <DetailRow label="Order ID" value={conv.order_id} mono />
            {staff && <DetailRow label="System ID" value={conv.system_id} mono />}
            <DetailRow label="Offer" value={conv.offer_name} />
            <DetailRow label="Stream" value={conv.stream_name} />
            <DetailRow label="Country" value={conv.country} mono />
          </Section>
          <Section title="Click & Sub IDs">
            <DetailRow label="IP" value={click.ip} mono />
            <DetailRow label="Referrer" value={click.referer} />
            <DetailRow label="Landing URL" value={click.landing_url} />
            <DetailRow label="User Agent" value={click.user_agent} />
            {canEditAny && perms.subs && form
              ? SUB_KEYS.map((k) => <EditField key={k} label={k}><input value={form[k]} onChange={(e) => set(k, e.target.value)} className={`${inp} font-mono`} data-testid={`edit-${k}`} /></EditField>)
              : SUB_KEYS.map((k) => <DetailRow key={k} label={k} value={click[k] || conv[k]} mono />)}
          </Section>
          <Section title="Device & Anti-Fraud">
            <DetailRow label="Device" value={click.device} />
            <DetailRow label="Browser" value={click.browser} />
            <DetailRow label="OS" value={click.os} />
            <DetailRow label="VPN" value={afVal(af.vpn)} />
            <DetailRow label="Proxy" value={afVal(af.proxy)} />
            <DetailRow label="Datacenter" value={afVal(af.datacenter)} />
            <DetailRow label="Bot score" value={af.checked ? String(af.bot_score ?? "—") : "Not checked"} />
          </Section>
          {staff && (
            <div className="sm:col-span-2">
              <Section title="Outbound Postback delivery">
                {(data?.postback_deliveries || []).length === 0 ? (
                  <p className="text-xs text-muted-foreground" data-testid="no-deliveries">No postback sent yet for this conversion.</p>
                ) : (
                  (data.postback_deliveries || []).map((d, i) => (
                    <div key={d.id || i} className="flex flex-wrap items-center gap-x-2 gap-y-1 border-b border-white/[0.05] py-1.5 text-xs last:border-0" data-testid={`delivery-${i}`}>
                      {d.success ? <CheckCircle2 className="h-3.5 w-3.5 text-[#22C55E]" /> : <XCircle className="h-3.5 w-3.5 text-[#EF4444]" />}
                      <span className={d.success ? "font-medium text-[#22C55E]" : "font-medium text-[#EF4444]"}>{d.success ? "Success" : "Error"}</span>
                      <span className="font-mono text-muted-foreground">HTTP {d.http_status ?? "—"}</span>
                      {d.event && <span className="text-muted-foreground/70">· {d.event}</span>}
                      <span className="ml-auto text-muted-foreground">{fmtDateTime(d.created_at)}{d.response_ms != null ? ` · ${d.response_ms}ms` : ""}</span>
                      {!d.success && d.error && <span className="w-full break-all text-[#EF4444]/80">“{d.error}”</span>}
                    </div>
                  ))
                )}
              </Section>
            </div>
          )}
          <div className="sm:col-span-2">
            <Section title="Status history">
              {(conv.status_history || []).length === 0 && <p className="text-xs text-muted-foreground">No changes yet.</p>}
              {(conv.status_history || []).map((h, i) => (
                <div key={i} className="flex items-center gap-2 border-b border-white/[0.05] py-1.5 text-xs last:border-0" data-testid={`history-${i}`}>
                  <StatusBadge status={h.to} />
                  <span className="text-muted-foreground">{h.from ? `from ${h.from}` : "created"}</span>
                  <span className="ml-auto text-muted-foreground">{h.by_name || h.by} · {fmtDateTime(h.at)}</span>
                  {h.comment && <span className="text-foreground/70">“{h.comment}”</span>}
                </div>
              ))}
            </Section>
          </div>
        </div>
        {canEditAny && (
          <div className="flex justify-end gap-2 border-t border-white/[0.08] pt-3">
            <Button variant="ghost" onClick={onClose} className="text-muted-foreground hover:text-foreground">Close</Button>
            <Button disabled={busy || !form} onClick={save} data-testid="detail-save" className="gap-1.5 bg-white text-black hover:bg-white/90">{busy ? "Saving..." : "Save changes"}</Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function StatusApplyBar({ ids, clear, busy, setBusy }) {
  const qc = useQueryClient();
  const [pay, setPay] = useState("");
  const apply = async () => {
    if (!pay) return toast.error("Choose a payment status to set");
    setBusy(true);
    try {
      const { data } = await http.post("/conversions/bulk-edit", { items: ids.map((id) => ({ id, payment_status: pay })) });
      qc.invalidateQueries({ queryKey: ["conversions"] });
      qc.invalidateQueries({ queryKey: ["balances"] });
      qc.invalidateQueries({ queryKey: ["dashboard"] });
      qc.invalidateQueries({ queryKey: ["statistics"] });
      qc.invalidateQueries({ queryKey: ["stats-report"] });
      toast.success("Payment status updated", { description: `${data.changed} conversion(s) updated` });
      setPay(""); clear();
    } catch (e) {
      toast.error("Update failed", { description: e?.response?.data?.detail || "Try again." });
    } finally { setBusy(false); }
  };
  const sel = "h-8 rounded-md border border-white/[0.12] bg-[#0A0A0A] px-2 text-xs text-foreground outline-none focus:border-white/25";
  return (
    <div className="flex items-center gap-1.5 rounded-lg border border-white/[0.08] bg-white/[0.02] px-2 py-1">
      <span className="text-[11px] uppercase tracking-wider text-muted-foreground/70">Set payment</span>
      <select value={pay} onChange={(e) => setPay(e.target.value)} className={sel} data-testid="set-payment-status">
        <option value="">Choose…</option><option value="hold">Hold</option><option value="approved">Approved</option><option value="trash">Trash</option>
      </select>
      <Button size="sm" disabled={busy || !pay} onClick={apply} data-testid="status-apply-btn" className="h-8 gap-1.5 bg-[#22C55E] text-xs text-black hover:bg-[#16a34a]"><Check className="h-3.5 w-3.5" /> Apply</Button>
    </div>
  );
}

export default function Conversions() {
  const { user } = useAuth();
  const opts = useOptions();
  const qc = useQueryClient();
  const staff = ["manager", "tenant_admin", "super_admin"].includes(user.role);
  const canDelete = staff && hasPerm(user, "conversions.delete");
  const canEditMoney = staff && hasPerm(user, "conversions.edit_money");
  const canEditDate = staff && hasPerm(user, "conversions.edit_date");
  const canEditSubs = staff && hasPerm(user, "conversions.edit_subids");
  const canEditType = staff && hasPerm(user, "conversions.edit_type");
  const canChangeOwner = staff && hasPerm(user, "conversions.change_owner");
  const canPayment = staff && hasPerm(user, "conversions.payment_status");
  const canResend = staff && hasPerm(user, "conversions.resend_postback");
  const canManualPb = staff && hasPerm(user, "conversions.manual_postback");
  const editScopes = [
    canEditMoney && ["money", "Money (revenue / payout)"],
    canEditDate && ["date", "Date / time"],
    canEditSubs && ["subs", "Sub IDs"],
    canEditType && ["status", "Conversion type (Lead / Sale / RevShare)"],
    (canEditMoney && canEditDate && canEditSubs && canEditType) && ["all", "All fields"],
  ].filter(Boolean);
  const { data: offersData } = useResourceList("offers", {});
  const { data: streamsData } = useResourceList("streams", {});
  const tcol = useTenantColumn("statistics");
  const { data: tenantsData } = useTenants();
  const [detailId, setDetailId] = useState(null);
  const [bulkPbOpen, setBulkPbOpen] = useState(false);
  const [editState, setEditState] = useState(null);
  const [ownerState, setOwnerState] = useState(null);
  const [delState, setDelState] = useState(null); // {ids, clear}
  const [delConfirm, setDelConfirm] = useState("");
  const [delBusy, setDelBusy] = useState(false);

  const runDelete = async () => {
    setDelBusy(true);
    try {
      const { data: res } = await http.post("/conversions/bulk-delete", { ids: delState.ids });
      toast.success(`${res.deleted} conversion(s) deleted`);
      qc.invalidateQueries({ queryKey: ["conversions"] });
      qc.invalidateQueries({ queryKey: ["balances"] });
      delState.clear?.();
      setDelState(null); setDelConfirm("");
    } catch (e) {
      toast.error("Delete failed", { description: e?.response?.data?.detail || "Try again." });
    } finally { setDelBusy(false); }
  };

  const offers = offersData?.items || [];
  const streams = streamsData?.items || [];
  const isBuyer = user.role === "buyer";
  // Buyers only see offers they actually worked with — offers with at least one of THEIR streams
  // (any status). Staff keep the full offers list.
  const offerFilterOptions = (() => {
    if (isBuyer) {
      const m = new Map();
      streams.forEach((s) => {
        if (s.offer_id && s.offer_name && s.offer_name !== "—" && !m.has(s.offer_id))
          m.set(s.offer_id, { value: s.offer_id, label: s.offer_name, search: `${s.offer_name} ${s.offer_seq ?? ""}` });
      });
      return [...m.values()];
    }
    return offers.map((o) => ({ value: o.id, label: `#${o.tenant_seq} ${o.name}`, search: `#${o.tenant_seq} ${o.name} ${o.geo || ""}` }));
  })();

  // Pre-applied filters when arriving from Statistics (deep-link with full context).
  const [searchParams] = useSearchParams();
  const initialFilters = useMemo(() => {
    const f = {};
    ["status", "payment_status", "offer_id", "stream_id", "country", "buyer_id", "advertiser_id"].forEach((k) => {
      const v = searchParams.get(k);
      if (v) f[k] = v.split(",").filter(Boolean);
    });
    return f;
  }, [searchParams]);
  const initialCustom = useMemo(() => {
    const c = {};
    const df = searchParams.get("date_from"), dt = searchParams.get("date_to");
    const sf = searchParams.get("sub_field"), sv = searchParams.get("sub_value");
    if (df) c.dateFrom = df;
    if (dt) c.dateTo = dt;
    if (sf) c.subField = sf;
    if (sv) c.subValue = sv;
    if (searchParams.get("exclude_trash")) c.excludeTrash = true;
    return c;
  }, [searchParams]);

  // ---- Server-side pagination: DataTablePro reports page/size/applied filters; we fetch one page.
  const [srv, setSrv] = useState(() => ({ page: 1, size: 10, q: "", filters: initialFilters, custom: initialCustom, sort: null, dir: "asc" }));
  const handleServerChange = React.useCallback((s) => setSrv(s), []);
  const qs = useMemo(() => {
    const p = new URLSearchParams();
    p.set("limit", String(srv.size));
    p.set("offset", String((srv.page - 1) * srv.size));
    if (srv.q.trim()) p.set("search", srv.q.trim());
    const f = srv.filters || {}, c = srv.custom || {};
    ["status", "payment_status", "offer_id", "stream_id", "buyer_id", "advertiser_id", "country", "tenant_id"].forEach((k) => {
      const v = f[k];
      if (Array.isArray(v) ? v.length : v && v !== "all") p.set(k, Array.isArray(v) ? v.join(",") : v);
    });
    if (c.dateFrom) p.set("date_from", c.dateFrom);
    if (c.dateTo) p.set("date_to", c.dateTo);
    if (c.dateFrom || c.dateTo) p.set("tz", getDisplayTimezone());
    if (c.excludeTrash) p.set("exclude_trash", "1");
    if (c.subField && (c.subValue || "").trim()) { p.set("sub_field", c.subField); p.set("sub_value", c.subValue.trim()); }
    const ids = parseClickIds(c.clickIds);
    if (ids) p.set("click_ids", [...ids].join(","));
    const oids = parseOrderIds(c.orderIds);
    if (oids) p.set("order_ids", [...oids].join(","));
    if (srv.sort) { p.set("sort", srv.sort); p.set("order", srv.dir || "asc"); }
    return p.toString();
  }, [srv]);
  const { data, isLoading } = useQuery({
    queryKey: ["conversions", "server", qs],
    queryFn: async () => (await http.get(`/conversions?${qs}`)).data,
    placeholderData: (prev) => prev,
  });
  const rows = data?.items || [];

  const applyResend = async (ids, clear, setBusy) => {
    setBusy(true);
    try {
      const { data: res } = await http.post("/conversions/bulk-resend-postback", { ids });
      if (res.no_postbacks) toast.info("No active postbacks", { description: "Selected buyers have no matching active outbound postbacks." });
      else toast.success("Postbacks resent", { description: `${res.deliveries} delivery(ies) across ${res.conversions} conversion(s)` });
      clear();
    } catch (e) {
      toast.error("Resend failed", { description: e?.response?.data?.detail || "Try again." });
    } finally { setBusy(false); }
  };

  const columns = [
    ...(staff ? [{ key: "system_id", label: "System ID", mono: true, exportValue: (r) => r.system_id, render: (r) => <IdCell value={r.system_id} testId="conv-system-id" /> }] : []),
    { key: "order_id", label: "Order ID", mono: true, exportValue: (r) => r.order_id, render: (r) => <IdCell value={r.order_id} strong testId="conv-order-id" /> },
    { key: "created_at", label: "Event Date", sortable: true, render: (r) => fmtDateTime(r.created_at) },
    { key: "click_date", label: "Click Date", render: (r) => r.click_date ? fmtDateTime(r.click_date) : "—" },
    ...(staff ? [{ key: "buyer", label: "Buyer", exportValue: (r) => r.buyer_label, render: (r) => <span className="text-foreground/90">{r.buyer_label || r.buyer_name}</span> }] : []),
    ...(staff ? [{ key: "advertiser", label: "Advertiser", exportValue: (r) => r.advertiser_name, render: (r) => r.advertiser_name }] : []),
    { key: "offer", label: "Offer", exportValue: (r) => r.offer_name, render: (r) => <span className="text-foreground/90">{r.offer_name}</span> },
    { key: "stream", label: "Stream", exportValue: (r) => r.stream_name, render: (r) => <span className="text-foreground/80">{r.stream_name}</span> },
    { key: "country", label: "Country", render: (r) => <span className="font-mono">{r.country || "—"}</span> },
    { key: "status", label: "Conversion", sortable: true, render: (r) => <StatusBadge status={r.status} /> },
    { key: "payment_status", label: "Payment", sortable: true, render: (r) => <StatusBadge status={r.payment_status} /> },
    ...(staff ? [{ key: "revenue", label: "Revenue", align: "right", mono: true, sortable: true, sortValue: (r) => Number(r.revenue || 0), exportValue: (r) => r.revenue, render: (r) => fmtMoney(r.revenue) }] : []),
    { key: "payout", label: "Payout", align: "right", mono: true, sortable: true, sortValue: (r) => Number(r.payout || 0), exportValue: (r) => r.payout, render: (r) => fmtMoney(r.payout) },
    ...(staff ? [{ key: "profit", label: "Profit", align: "right", mono: true, sortable: true, sortValue: (r) => Number(r.profit || 0), exportValue: (r) => r.profit, render: (r) => <span className="text-[#22C55E]">{fmtMoney(r.profit)}</span> }] : []),
    ...SUB_KEYS.map((k) => ({ key: k, label: k.replace("sub_id_", "S"), render: (r) => <span className="font-mono text-xs text-muted-foreground">{r[k] || "—"}</span> })),
  ];

  const filters = [
    ...(tcol.show ? [{ key: "tenant_id", label: "Tenant", getValue: (r) => r.tenant_id, options: (tenantsData?.items || []).map((t) => ({ value: t.internalId, label: t.name })) }] : []),
    { key: "status", label: "Conversion", multi: true, getValue: (r) => r.status, options: asOpts([{ value: "lead", label: "Lead" }, { value: "sale", label: "Sale" }, { value: "redep", label: "Redep" }, { value: "revshare", label: "RevShare" }]) },
    { key: "payment_status", label: "Payment", multi: true, getValue: (r) => r.payment_status, options: asOpts([{ value: "hold", label: "Hold" }, { value: "approved", label: "Approved" }, { value: "trash", label: "Trash" }]) },
    { key: "offer_id", label: "Offer", multi: true, getValue: (r) => r.offer_id, options: offerFilterOptions },
    { key: "stream_id", label: "Stream", multi: true, getValue: (r) => r.stream_id, options: streams.map((s) => ({ value: s.id, label: `#${s.tenant_seq} — ${s.name || "Stream"}`, search: `#${s.tenant_seq} ${s.tenant_seq} ${s.name || ""} ${s.stream_key || ""}` })) },
    { key: "country", label: "Country", country: true, multi: true, getValue: (r) => r.country },
    ...(staff ? [{ key: "buyer_id", label: "Buyer", multi: true, getValue: (r) => r.buyer_id, options: userOptions(opts.users) }] : []),
    ...(staff ? [{ key: "advertiser_id", label: "Advertiser", multi: true, getValue: (r) => r.advertiser_id, options: (opts.advertisers || []).map((a) => ({ value: a.id, label: a.name, search: a.name })) }] : []),
  ];

  const rowActions = (row) => [
    { label: "View details", icon: Eye, onClick: () => setDetailId(row.id), testId: `view-conversion-${row.id}` },
    ...(canDelete ? [{ label: "Delete", icon: Trash2, danger: true, onClick: () => setDelState({ ids: [row.id] }), testId: `delete-conversion-${row.id}` }] : []),
  ];

  return (
    <div className="space-y-5">
      <PageHeader title="Conversions" subtitle={staff ? "Approve holds, reconcile payouts and audit the full conversion lifecycle." : "Your conversions — Hold awaits approval, Approved becomes withdrawable."} />

      <DataTablePro testId="conversions-table" columns={tcol.inject(columns)} rows={rows} loading={isLoading && !data}
        serverMode serverTotal={data?.total} onServerChange={handleServerChange} fetchAllRows={() => fetchAllPages("/conversions", qs)}
        filters={filters} searchKeys={staff ? ["order_id", "system_id"] : ["order_id"]}
        searchPlaceholder={staff ? "Search by Order ID or System ID..." : "Search by Order ID..."} exportName="conversions"
        rowActions={rowActions} emptyText="No conversions recorded."
        initialFilters={initialFilters} initialCustom={initialCustom}
        customFilters={({ values, set }) => (
          <div className="w-full sm:w-[190px]">
            <DateRangePicker from={values.dateFrom} to={values.dateTo}
              onChange={({ from, to }) => { set("dateFrom", from); set("dateTo", to); }}
              placeholder="Date range" testId="conv-date-range" />
          </div>
        )}
        customFiltersEnd={({ values, set, onEnter }) => (
          <>
            <div className="w-full sm:w-[150px]">
              <Select value={values.subField || "none"} onValueChange={(v) => set("subField", v === "none" ? "" : v)}>
                <SelectTrigger className="h-9 w-full rounded-lg border-white/[0.1] bg-[#0A0A0A] text-sm" data-testid="conv-sub-field"><SelectValue placeholder="Sub ID field" /></SelectTrigger>
                <SelectContent className="border-white/[0.1] bg-[#1a1a1e]">
                  <SelectItem value="none">Sub ID field</SelectItem>
                  {SUB_KEYS.map((k) => <SelectItem key={k} value={k}>{k}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <Input value={values.subValue || ""} onChange={(e) => set("subValue", e.target.value)} onKeyDown={onEnter}
              placeholder="Sub ID value" className="h-9 w-full rounded-lg border-white/[0.1] bg-[#0A0A0A] text-sm sm:w-[150px]" data-testid="conv-sub-value" />
            {staff && <ClickIdListFilter value={values.clickIds || ""} onChange={(v) => set("clickIds", v)} testId="conv-clickids" />}
            <ClickIdListFilter value={values.orderIds || ""} onChange={(v) => set("orderIds", v)} testId="conv-orderids"
              label="Order IDs" title="Filter by Order ID" parse={parseOrderIds} />
          </>
        )}
        toolbarRight={canManualPb ? (
          <Button onClick={() => setBulkPbOpen(true)} data-testid="bulk-postback-btn" className="h-9 gap-1.5 rounded-lg bg-white text-black hover:bg-white/90"><Send className="h-4 w-4" /> Bulk Postback</Button>
        ) : undefined}
        selectable={staff} bulkActions={[]}
        bulkExtra={staff ? ({ ids, clear, busy, setBusy }) => (
          <div className="flex flex-wrap items-center gap-1.5">
            {(editScopes.length > 0 || canChangeOwner) && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button size="sm" disabled={busy} data-testid="bulk-edit-btn" className="h-8 gap-1.5 bg-white text-xs text-black hover:bg-white/90"><Pencil className="h-3.5 w-3.5" /> Edit ▾</Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="start" className="border-white/[0.1] bg-[#1a1a1e]">
                  {editScopes.map(([s, label]) => (
                    <DropdownMenuItem key={s} data-testid={`edit-scope-${s}`} onClick={() => setEditState({ rows: rows.filter((r) => ids.includes(r.id)), clear, scope: s })} className="cursor-pointer text-sm focus:bg-white/[0.06]">{label}</DropdownMenuItem>
                  ))}
                  {canChangeOwner && (
                    <DropdownMenuItem data-testid="edit-scope-owner" onClick={() => setOwnerState({ rows: rows.filter((r) => ids.includes(r.id)), clear })} className="cursor-pointer text-sm focus:bg-white/[0.06]">Change owner</DropdownMenuItem>
                  )}
                </DropdownMenuContent>
              </DropdownMenu>
            )}
            {canResend && <Button size="sm" disabled={busy} onClick={() => applyResend(ids, clear, setBusy)} data-testid="bulk-resend-btn" className="h-8 gap-1.5 border border-white/15 bg-transparent text-xs text-foreground hover:bg-white/5"><RefreshCw className="h-3.5 w-3.5" /> Resend Postback</Button>}
            {canDelete && <Button size="sm" disabled={busy} onClick={() => setDelState({ ids, clear })} data-testid="bulk-delete-btn" className="h-8 gap-1.5 border border-[#EF4444]/40 bg-transparent text-xs text-[#EF4444] hover:bg-[#EF4444]/10"><Trash2 className="h-3.5 w-3.5" /> Delete</Button>}
            {canPayment && <StatusApplyBar ids={ids} clear={clear} busy={busy} setBusy={setBusy} />}
          </div>
        ) : undefined} />

      {detailId && <ConversionDetail id={detailId} staff={staff} perms={{ money: canEditMoney, date: canEditDate, subs: canEditSubs, type: canEditType, payment: canPayment }} onClose={() => setDetailId(null)} />}
      {canManualPb && <BulkPostbackDialog open={bulkPbOpen} onOpenChange={setBulkPbOpen} />}
      {staff && editState && (
        <ConversionBulkEditDialog open onOpenChange={(o) => { if (!o) setEditState(null); }}
          rows={editState.rows} scope={editState.scope} onDone={() => editState.clear?.()} />
      )}
      {staff && ownerState && (
        <ConversionOwnerDialog open onOpenChange={(o) => { if (!o) setOwnerState(null); }}
          rows={ownerState.rows} onDone={() => ownerState.clear?.()} />
      )}

      <Dialog open={!!delState} onOpenChange={(o) => { if (!o) { setDelState(null); setDelConfirm(""); } }}>
        <DialogContent className="max-w-md border-white/[0.1] bg-[#141417]" data-testid="conversion-delete-dialog">
          <DialogHeader><DialogTitle className="flex items-center gap-2 text-[#EF4444]"><Trash2 className="h-5 w-5" /> Delete conversion{(delState?.ids?.length || 0) > 1 ? "s" : ""}</DialogTitle></DialogHeader>
          <p className="text-sm text-muted-foreground">
            You are about to permanently delete <span className="font-semibold text-foreground">{delState?.ids?.length || 0}</span> conversion{(delState?.ids?.length || 0) > 1 ? "s" : ""}. This reverses any approved revenue on the related streams and cannot be undone. Delivered postbacks &amp; logs are kept for audit.
          </p>
          {(delState?.ids?.length || 0) > 1 && (
            <div className="mt-2">
              <label className="text-xs text-muted-foreground">Type <span className="font-mono text-foreground">DELETE</span> to confirm bulk deletion</label>
              <input value={delConfirm} onChange={(e) => setDelConfirm(e.target.value)} data-testid="bulk-delete-confirm-input"
                className="mt-1 w-full rounded-lg border border-white/[0.12] bg-[#0A0A0A] px-3 py-2 text-sm text-foreground" placeholder="DELETE" />
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => { setDelState(null); setDelConfirm(""); }} className="border-white/[0.12]">Cancel</Button>
            <Button disabled={delBusy || ((delState?.ids?.length || 0) > 1 && delConfirm.trim().toUpperCase() !== "DELETE")}
              onClick={runDelete} data-testid="conversion-delete-confirm-btn" className="bg-[#EF4444] hover:bg-[#EF4444]/90">Delete permanently</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

